// Sync queue: every offline operation gets a local UUID + device timestamp and is replayed in order, idempotently.
import axios from "axios";
import { v4 as uuid } from "uuid";
import { storage } from "./storage";
import { getState, setState, isNetworkError } from "./state";

const raw = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });
const COALESCE = new Set(["take5.save", "inspection.save"]);
let flushing = null;
let started = false;
const SYNCED_VISIBLE_MS = 4000;

const ENTITY_PATH = { take5: "take5s", inspection: "inspections" };
const LABEL = { "take5.create": "Création Take 5", "take5.save": "Brouillon Take 5", "take5.authorize": "Autorisation Take 5",
  "take5.stop": "Arrêt du travail", "take5.complete": "Tâche terminée", "inspection.create": "Création inspection",
  "inspection.save": "Brouillon inspection", "inspection.finalize": "Finalisation inspection", "team.compose": "Équipe du jour",
  "photo.upload": "Photo" };

const outbox = async () => (await storage.all("outbox")).sort((a, b) => a.key - b.key);
// Offline data is isolated per user + workplace: only the active workplace's operations are counted and sent.
const mine = (o, user) => o.user_id === user.id && (o.org_id || user.org_id) === user.org_id;

export async function enqueue(user, type, entityId, payload) {
  const ops = await outbox();
  if (COALESCE.has(type)) {
    const last = ops.filter((o) => o.value.entity_id === entityId).pop();
    if (last && last.value.type === type && last.value.status === "pending") await storage.del("outbox", last.key);
  }
  const seq = Date.now() * 1000 + Math.floor(Math.random() * 1000);
  await storage.put("outbox", seq, { seq, op_id: uuid(), user_id: user.id, org_id: user.org_id, type, entity_id: entityId, device_at: new Date().toISOString(), payload, status: "pending" });
  await refreshCounts();
  setTimeout(() => flush(), 50);
}

export async function hasPending(entityId) {
  return (await outbox()).some((o) => o.value.entity_id === entityId || o.value.payload?.linked_id === entityId);
}

export async function conflictFor(entityId) {
  return (await outbox()).find((o) => o.value.status === "conflict" && (o.value.entity_id === entityId || o.value.payload?.linked_id === entityId))?.value.detail || null;
}

async function refreshCounts() {
  const user = await storage.get("cache", "/auth/me");
  const ops = (await outbox()).map((o) => o.value).filter((o) => !user || mine(o, user));
  const entities = Object.fromEntries(ops.map((o) => [o.type === "photo.upload" ? o.payload.linked_id : o.entity_id, o.status])
    .sort((a, b) => (a[1] === "conflict") - (b[1] === "conflict")));
  const done = Object.keys(getState().entities).filter((id) => !entities[id]);
  if (done.length) {
    setState({ justSynced: { ...getState().justSynced, ...Object.fromEntries(done.map((id) => [id, true])) } });
    setTimeout(() => {
      const rest = { ...getState().justSynced };
      done.forEach((id) => delete rest[id]);
      setState({ justSynced: rest });
    }, SYNCED_VISIBLE_MS);
  }
  setState({
    pending: ops.length,
    entities,
    conflicts: ops.filter((o) => o.status === "conflict").map((o) => ({ seq: o.seq, type: o.type, label: LABEL[o.type] || o.type, entity_id: o.entity_id, detail: o.detail, device_at: o.device_at })),
    lastSync: await storage.get("meta", "lastSync"),
  });
}

async function call(fn) {
  try {
    return await fn();
  } catch (e) {
    if (e.response?.status !== 401) throw e;
    await raw.post("/auth/refresh");
    return fn();
  }
}

async function sendOp(op) {
  if (op.type === "photo.upload") {
    const p = await storage.get("photos", op.entity_id);
    if (!p) return { status: "ok" };
    const form = new FormData();
    form.append("file", p.blob, `${op.entity_id}.${(p.content_type || "image/jpeg").split("/")[1]}`);
    form.append("linked_type", p.linked_type);
    form.append("linked_id", p.linked_id);
    form.append("client_id", op.entity_id);
    try {
      await call(() => raw.post("/files", form));
      return { status: "ok", uploaded: true };
    } catch (e) {
      if (e.response && e.response.status >= 400 && e.response.status < 500 && e.response.status !== 401) {
        return { status: "conflict", detail: e.response.data?.detail || "Photo refusée par le serveur" };
      }
      throw e;
    }
  }
  const { op_id, type, entity_id, device_at, payload } = op;
  const r = await call(() => raw.post("/sync/ops", { op_id, type, entity_id, device_at, payload }));
  return r.data;
}

const detailText = (d) => (typeof d === "string" ? d : d?.message ? `${d.message} ${Object.values(d.errors || {}).join(" ")}` : "Refusé par le serveur");

async function settleRecords() {
  const ops = (await outbox()).map((o) => o.value);
  for (const { key, value } of await storage.all("records")) {
    const id = value.doc.id;
    if (!ops.some((o) => o.entity_id === id || o.payload?.linked_id === id)) {
      await storage.put("cache", `/${ENTITY_PATH[value.kind]}/${id}`, value.doc);
      await storage.del("records", key);
    }
  }
}

async function runFlush() {
  const user = await storage.get("cache", "/auth/me");
  if (!user) return;
  const blocked = new Set();
  let changed = false;
  for (const { key, value: op } of await outbox()) {
    if (!mine(op, user)) continue;
    const entity = op.type === "photo.upload" ? op.payload.linked_id : op.entity_id;
    if (op.status === "conflict") { blocked.add(entity); continue; }
    if (blocked.has(entity)) continue;
    let res;
    setState({ sending: entity });
    try {
      res = await sendOp(op);
    } catch (e) {
      if (isNetworkError(e)) setState({ online: false });
      break;
    } finally {
      setState({ sending: null });
    }
    setState({ online: true });
    if (res.status === "ok") {
      changed = true;
      await storage.del("outbox", key);
      if (op.type === "photo.upload") await storage.del("photos", op.entity_id);
      const doc = res.data;
      const kind = op.type.split(".")[0];
      if (doc?.id === op.entity_id && doc.status && ENTITY_PATH[kind]) {
        const rec = await storage.get("records", `${kind}:${doc.id}`);
        if (rec) await storage.put("records", `${kind}:${doc.id}`, { ...rec, doc: { ...rec.doc, ...doc } });
      }
      if (res.warnings?.length) {
        const notices = (await storage.get("meta", "notices")) || [];
        await storage.put("meta", "notices", [...notices, ...res.warnings.map((w) => ({ id: uuid(), text: w, at: new Date().toISOString() }))]);
      }
    } else {
      blocked.add(entity);
      await storage.put("outbox", key, { ...op, status: "conflict", detail: detailText(res.detail) });
    }
  }
  await settleRecords();
  if (changed) await refreshBundle();
  await refreshCounts();
}

export function flush() {
  flushing = flushing || runFlush().catch(() => {}).finally(() => { flushing = null; });
  return flushing;
}

const CACHE_KEYS = (b) => [["/referentials", b.referentials], ["/equipment", b.equipment], ["/teams/mine", b.teams], ["/activities", b.activities],
  ...Object.entries(b.candidates || {}).map(([id, list]) => [`/teams/${id}/candidates`, list])];

export async function refreshBundle() {
  try {
    const [b, me] = await Promise.all([
      call(() => raw.get("/offline/bundle", { params: { day: new Date().toLocaleDateString("en-CA") } })),
      call(() => raw.get("/auth/me")),
    ]);
    for (const [k, v] of CACHE_KEYS(b.data)) await storage.put("cache", k, v);
    await storage.put("cache", "/auth/me", me.data);
    await storage.put("meta", "bundle", { templates: b.data.templates, type_categories: b.data.type_categories, server_time: b.data.server_time });
    await storage.put("meta", "lastSync", Date.now());
    setState({ online: true });
    await refreshCounts();
  } catch (e) {
    if (isNetworkError(e)) setState({ online: false });
  }
}

export async function dropConflict(seq) {
  const op = await storage.get("outbox", seq);
  if (!op) return;
  const entity = op.type === "photo.upload" ? op.payload.linked_id : op.entity_id;
  for (const { key, value } of await outbox()) {
    const e = value.type === "photo.upload" ? value.payload.linked_id : value.entity_id;
    if (key >= seq && e === entity) {
      await storage.del("outbox", key);
      if (value.type === "photo.upload") await storage.del("photos", value.entity_id);
    }
  }
  await settleRecords();
  await refreshCounts();
  flush();
}

export async function takeNotices() {
  const n = (await storage.get("meta", "notices")) || [];
  return n;
}
export async function clearNotice(id) {
  await storage.put("meta", "notices", ((await storage.get("meta", "notices")) || []).filter((x) => x.id !== id));
}

// Logout keeps only what still has to be sent (and its photos/records); everything else is removed from the device.
export async function clearLocalSession() {
  await settleRecords();
  await storage.clear("cache");
  for (const k of ["bundle", "lastSync", "notices"]) await storage.del("meta", k);
  setState({ pending: getState().pending, lastSync: null });
}

export function startSync() {
  if (started) {
    refreshBundle().then(flush);
    return;
  }
  started = true;
  window.addEventListener("online", () => { setState({ online: true }); flush().then(refreshBundle); });
  window.addEventListener("offline", () => setState({ online: false }));
  setInterval(() => flush(), 20000);
  setInterval(() => refreshBundle(), 10 * 60000);
  refreshBundle().then(flush);
}

export { refreshCounts };
